/* eslint-disable react-refresh/only-export-components -- one public module by
   contract: onboarding imports `SocialsFields` + `useSaveSocials` from here. */
/**
 * Profile social handles (Instagram, TikTok, X) — the web twin of the app's
 * `app/src/utils/socials.ts` + `SocialLinks.tsx`. Handle rules match the
 * backend's `_clean_socials` (docs/api-schema.md §PUT /v1/profile): a pasted
 * profile URL or a leading "@" normalizes to the bare handle, and `socials`
 * REPLACES the stored map, so a save always sends the whole cleaned map
 * (`{}` clears every link).
 *
 * - `SocialsFields` — controlled inputs (raw drafts; normalized on blur).
 * - `useSaveSocials()` — the `PUT /v1/profile { socials }` mutation.
 * - `SocialLinks` — the quiet icon + handle links on a profile masthead.
 */
import { useId, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { OwnProfile, SocialPlatform, Socials } from '@shared/types';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { userKey } from '@/features/social/api';
import { translate, useLocaleOptional } from '@/i18n/locale';
import { cn } from '@/lib/utils';

import { MY_PROFILE_QUERY_KEY, updateProfile } from './api';

/* --- pure helpers ------------------------------------------------------------ */

export const SOCIAL_PLATFORMS: readonly SocialPlatform[] = ['instagram', 'tiktok', 'x'];

const HANDLE_RE: Record<SocialPlatform, RegExp> = {
  instagram: /^[A-Za-z0-9._]{1,30}$/,
  tiktok: /^[A-Za-z0-9._]{2,24}$/,
  x: /^[A-Za-z0-9_]{1,15}$/,
};
const PROFILE_URL_RE =
  /^(?:https?:\/\/)?(?:[\w-]+\.)*(?:instagram\.com|tiktok\.com|x\.com|twitter\.com)\/(@?[^/?#]+)/i;

/** A pasted profile URL or "@handle" → the bare handle; '' when blank. */
export function normalizeSocialHandle(raw: string): string {
  const value = raw.trim();
  return (PROFILE_URL_RE.exec(value)?.[1] ?? value).replace(/^@+/, '');
}

/** A blank handle is valid (it clears that platform). */
export function isValidSocialHandle(platform: SocialPlatform, handle: string): boolean {
  return handle === '' || HANDLE_RE[platform].test(handle);
}

/** The PUT payload: every platform normalized, blanks dropped. */
export function cleanSocials(value: Socials): Socials {
  const out: Socials = {};
  for (const platform of SOCIAL_PLATFORMS) {
    const handle = normalizeSocialHandle(value[platform] ?? '');
    if (handle) out[platform] = handle;
  }
  return out;
}

/** Every platform's (normalized) draft is acceptable. */
export function socialsAreValid(value: Socials): boolean {
  return SOCIAL_PLATFORMS.every((p) => isValidSocialHandle(p, normalizeSocialHandle(value[p] ?? '')));
}

/** Same links after normalization (drives "dirty"). */
export function socialsEqual(a: Socials, b: Socials): boolean {
  const ca = cleanSocials(a);
  const cb = cleanSocials(b);
  return SOCIAL_PLATFORMS.every((p) => ca[p] === cb[p]);
}

/** Only the platforms with a handle, in display order. */
export function socialEntries(socials: Socials): { platform: SocialPlatform; handle: string }[] {
  return SOCIAL_PLATFORMS.flatMap((platform) =>
    socials[platform] ? [{ platform, handle: socials[platform]! }] : [],
  );
}

export function socialUrl(platform: SocialPlatform, handle: string): string {
  const h = encodeURIComponent(handle);
  if (platform === 'instagram') return `https://www.instagram.com/${h}`;
  if (platform === 'tiktok') return `https://www.tiktok.com/@${h}`;
  return `https://x.com/${h}`;
}

/* --- brand marks (monochrome, currentColor; lucide has no brand icons) ------- */

const MARK_PATHS: Record<SocialPlatform, string> = {
  instagram:
    'M12 2.2c3.2 0 3.6 0 4.8.1 3.3.1 4.8 1.7 4.9 4.9.1 1.3.1 1.6.1 4.8s0 3.6-.1 4.8c-.1 3.2-1.7 4.8-4.9 4.9-1.3.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-3.3-.1-4.8-1.7-4.9-4.9C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.8C2.4 3.9 3.9 2.4 7.2 2.3 8.4 2.2 8.8 2.2 12 2.2zM12 0C8.7 0 8.3 0 7.1.1 2.7.3.3 2.7.1 7.1 0 8.3 0 8.7 0 12s0 3.7.1 4.9c.2 4.4 2.6 6.8 7 7 1.2.1 1.6.1 4.9.1s3.7 0 4.9-.1c4.4-.2 6.8-2.6 7-7 .1-1.2.1-1.6.1-4.9s0-3.7-.1-4.9c-.2-4.4-2.6-6.8-7-7C15.7 0 15.3 0 12 0zm0 5.8a6.2 6.2 0 1 0 0 12.4 6.2 6.2 0 0 0 0-12.4zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.4-11.8a1.4 1.4 0 1 0 0 2.9 1.4 1.4 0 0 0 0-2.9z',
  tiktok:
    'M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
  x: 'M18.9 1.2h3.7l-8 9.2L24 22.8h-7.4l-5.8-7.6-6.6 7.6H.5l8.6-9.8L0 1.2h7.6l5.2 6.9 6.1-6.9zm-1.3 19.4h2L6.5 3.3H4.3l13.3 17.3z',
};

export function SocialMark({ platform, className }: { platform: SocialPlatform; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={cn('h-4 w-4 shrink-0', className)}>
      <path fill="currentColor" d={MARK_PATHS[platform]} />
    </svg>
  );
}

/* --- display ----------------------------------------------------------------- */

/** Quiet ink links (icon + @handle), new tab. Renders nothing when empty. */
export function SocialLinks({ socials, className }: { socials: Socials; className?: string }) {
  const { t } = useLocaleOptional();
  const entries = socialEntries(socials);
  if (entries.length === 0) return null;
  return (
    <ul aria-label={t('profile.socials.title')} className={cn('flex flex-wrap gap-x-4 gap-y-2', className)}>
      {entries.map(({ platform, handle }) => (
        <li key={platform}>
          <a
            href={socialUrl(platform, handle)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('profile.socials.open', { network: t(`profile.socials.${platform}`), handle })}
            className="inline-flex min-h-6 items-center gap-1.5 rounded text-caption text-ink-secondary transition-colors hover:text-ink"
          >
            <SocialMark platform={platform} className="h-3.5 w-3.5" />
            <span>@{handle}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

/* --- edit -------------------------------------------------------------------- */

/**
 * One labelled input per platform with a fixed "@" prefix. `value` holds raw
 * drafts (a URL is fine); blur normalizes the field to the bare handle.
 * Invalid handles get inline, announced text next to the field.
 */
export function SocialsFields({
  value,
  onChange,
  disabled,
}: {
  value: Socials;
  onChange: (next: Socials) => void;
  disabled?: boolean;
}) {
  const { t } = useLocaleOptional();
  const id = useId();
  return (
    <div className="flex flex-col gap-4">
      {SOCIAL_PLATFORMS.map((platform) => {
        const raw = value[platform] ?? '';
        const invalid = !isValidSocialHandle(platform, normalizeSocialHandle(raw));
        const network = t(`profile.socials.${platform}`);
        const inputId = `${id}-${platform}`;
        const errorId = `${inputId}-error`;
        return (
          <div key={platform} className="flex flex-col gap-1.5">
            <Label htmlFor={inputId} className="flex items-center gap-2 text-subhead text-ink">
              <SocialMark platform={platform} />
              {network}
            </Label>
            <div className="relative">
              <span
                aria-hidden
                className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-body text-ink-tertiary"
              >
                @
              </span>
              <Input
                id={inputId}
                value={raw}
                onChange={(event) => onChange({ ...value, [platform]: event.target.value })}
                onBlur={() => {
                  const handle = normalizeSocialHandle(raw);
                  if (handle !== raw) onChange({ ...value, [platform]: handle });
                }}
                className="pl-8"
                maxLength={100}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                inputMode="url"
                disabled={disabled}
                aria-invalid={invalid}
                aria-describedby={invalid ? errorId : undefined}
              />
            </div>
            {invalid && (
              <p id={errorId} role="alert" className="text-caption text-danger">
                {t('profile.socials.invalid', { network })}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * `PUT /v1/profile { socials }` with the whole cleaned map. On success the
 * `['myProfile']` cache takes the server's profile (and the self user card
 * re-reads); on failure a toast offers Retry. `mutate` ignores calls while a
 * save is in flight, so a double click never double-submits.
 */
export function useSaveSocials() {
  const qc = useQueryClient();
  const busy = useRef(false);
  const mutateRef = useRef<(socials: Socials) => void>(() => {});
  const mutation = useMutation<OwnProfile, Error, Socials>({
    mutationFn: (socials: Socials) => updateProfile({ socials: cleanSocials(socials) }),
    onSuccess: (profile) => {
      qc.setQueryData(MY_PROFILE_QUERY_KEY, profile);
      void qc.invalidateQueries({ queryKey: userKey(profile.username) });
    },
    onError: (_err, socials) => {
      toast.error(translate('profile.socials.saveError'), {
        action: { label: translate('common.retry'), onClick: () => mutateRef.current(socials) },
      });
    },
    onSettled: () => {
      busy.current = false;
    },
  });
  const mutate: typeof mutation.mutate = (socials, options) => {
    if (busy.current) return;
    busy.current = true;
    mutation.mutate(socials, options);
  };
  mutateRef.current = (socials) => mutate(socials);
  return { ...mutation, mutate };
}
